export type Vec3 = { x: number; y: number; z: number };
export type Intrinsics = { fx: number; fy: number; cx: number; cy: number };
export type DistCoeffs = { k1: number; k2: number; p1: number; p2: number; k3: number };
export type Pt2 = { u: number; v: number };

export const DEG2RAD = Math.PI / 180;

// device (X right, Y forward, Z up) -> camera (X right, Y down, Z forward)
export const R_cam_from_device: number[][] = [
    [1, 0, 0],
    [0, 0, -1],
    [0, 1, 0],
];

export function mulMat3Vec3(R: number[][], p: Vec3): Vec3 {
    return {
        x: R[0][0] * p.x + R[0][1] * p.y + R[0][2] * p.z,
        y: R[1][0] * p.x + R[1][1] * p.y + R[1][2] * p.z,
        z: R[2][0] * p.x + R[2][1] * p.y + R[2][2] * p.z,
    };
}

export function Rx(pitch: number): number[][] {
    const c = Math.cos(pitch), s = Math.sin(pitch);
    return [
        [1, 0, 0],
        [0, c, -s],
        [0, s, c],
    ];
}

// roll about Y (forward axis in device frame)
export function Ry(roll: number): number[][] {
    const c = Math.cos(roll), s = Math.sin(roll);
    return [
        [c, 0, s],
        [0, 1, 0],
        [-s, 0, c],
    ];
}

export function matMul3(A: number[][], B: number[][]): number[][] {
    const out = Array.from({ length: 3 }, () => [0, 0, 0]);
    for (let i = 0; i < 3; i++) {
        for (let j = 0; j < 3; j++) {
            out[i][j] = A[i][0] * B[0][j] + A[i][1] * B[1][j] + A[i][2] * B[2][j];
        }
    }
    return out;
}

export function distortNormalized(x: number, y: number, d: DistCoeffs): { xd: number; yd: number } {
    const { k1, k2, p1, p2, k3 } = d;
    const r2 = x * x + y * y;
    const r4 = r2 * r2;
    const r6 = r4 * r2;

    const radial = 1 + k1 * r2 + k2 * r4 + k3 * r6;
    const x_tan = 2 * p1 * x * y + p2 * (r2 + 2 * x * x);
    const y_tan = p1 * (r2 + 2 * y * y) + 2 * p2 * x * y;

    return { xd: x * radial + x_tan, yd: y * radial + y_tan };
}

export function projectCamToPixelDistorted(pCam: Vec3, intr: Intrinsics, dist: DistCoeffs): Pt2 | null {
    const { x: Xc, y: Yc, z: Zc } = pCam;
    if (!isFinite(Zc) || Zc <= 1e-6) return null;

    const x = Xc / Zc;
    const y = Yc / Zc;

    const { xd, yd } = distortNormalized(x, y, dist);

    const u = intr.fx * xd + intr.cx;
    const v = intr.fy * yd + intr.cy;

    if (!isFinite(u) || !isFinite(v)) return null;
    return { u, v };
}

// Convex hull (Monotonic chain). Returns hull points in CCW order.
export function convexHull(points: Pt2[]): Pt2[] {
    if (points.length <= 2) return points.slice();

    const pts = points
        .slice()
        .sort((a, b) => (a.u === b.u ? a.v - b.v : a.u - b.u));

    const cross = (o: Pt2, a: Pt2, b: Pt2) =>
        (a.u - o.u) * (b.v - o.v) - (a.v - o.v) * (b.u - o.u);

    const lower: Pt2[] = [];
    for (const p of pts) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
        }
        lower.push(p);
    }

    const upper: Pt2[] = [];
    for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
        }
        upper.push(p);
    }

    upper.pop();
    lower.pop();
    return lower.concat(upper);
}

export function linspace(a: number, b: number, n: number): number[] {
    if (n <= 1) return [a];
    const step = (b - a) / (n - 1);
    return Array.from({ length: n }, (_, i) => a + i * step);
}

export function thickenSegmentToPolygon(p1: Pt2, p2: Pt2, halfWidthPx: number): Pt2[] {
    const dx = p2.u - p1.u;
    const dy = p2.v - p1.v;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return [];

    const nx = -dy / len;
    const ny = dx / len;

    const a = { u: p1.u + nx * halfWidthPx, v: p1.v + ny * halfWidthPx };
    const b = { u: p2.u + nx * halfWidthPx, v: p2.v + ny * halfWidthPx };
    const c = { u: p2.u - nx * halfWidthPx, v: p2.v - ny * halfWidthPx };
    const d = { u: p1.u - nx * halfWidthPx, v: p1.v - ny * halfWidthPx };

    return [a, b, c, d, a];
}

/**
 * Build an image polygon region where a radar detection could be, using:
 * - azimuth uncertainty (dAz)
 * - elevation beam halfwidth (beamHalf)
 * - range uncertainty (dR)
 * - device pitch/roll and height to constrain z_world into [zMin,zMax]
 */
export function radarDetectionToImageRegion(
    az: number,
    range: number,
    intr: Intrinsics,
    dist: DistCoeffs,
    pitch: number,
    roll: number,
    sensorHeight: number,
    dAzRad = 1.5 * DEG2RAD,
    beamHalfRad = 20 * DEG2RAD,
    dR = .5,
    zMin = 2.0,
    zMax = 3.0
): Pt2[] {
    const R_world_from_device = matMul3(Ry(roll), Rx(pitch));

    const azSamples = [az - dAzRad, az + dAzRad];
    const elSamples = linspace(-beamHalfRad, +beamHalfRad, 64);
    const rSamples = [Math.max(0.1, range - dR), range + dR];

    const imgPts: Pt2[] = [];

    for (const azS of azSamples) {
        for (const elS of elSamples) {
            const cosEl = Math.cos(elS);
            const dirDev: Vec3 = {
                x: Math.sin(azS) * cosEl,
                y: Math.cos(azS) * cosEl,
                z: Math.sin(elS),
            };

            const dirW = mulMat3Vec3(R_world_from_device, dirDev);

            for (const rS of rSamples) {
                const pW: Vec3 = {
                    x: rS * dirW.x,
                    y: rS * dirW.y,
                    z: sensorHeight + rS * dirW.z,
                };

                if (pW.z < zMin || pW.z > zMax) continue;

                // Projection is done in CAMERA frame; world-frame point pW is only used for the height filter.
                const pDev: Vec3 = { x: rS * dirDev.x, y: rS * dirDev.y, z: rS * dirDev.z };
                const pCam = mulMat3Vec3(R_cam_from_device, pDev);

                const pix = projectCamToPixelDistorted(pCam, intr, dist);
                if (pix) imgPts.push(pix);
            }
        }
    }

    if (imgPts.length >= 3) return convexHull(imgPts);
    if (imgPts.length === 2) return thickenSegmentToPolygon(imgPts[0], imgPts[1], 6);
    return [];
}

export type PolygonChartDataset = {
    label: string;
    data: { x: number; y: number; z: number }[];
    color: string;
    borderColor: string;
    borderWidth: number;
    pointRadius: number;
    showLine: boolean;
    fill: boolean;
    backgroundColor: string;
};

export function polygonToChartDataset(
    poly: Pt2[],
    label: string,
    color: string,
    imageHeight: number
): PolygonChartDataset | null {
    if (poly.length < 3) return null;

    const data = poly.map(p => ({
        x: p.u,
        y: imageHeight - p.v,
        z: 0,
    }));
    data.push({ ...data[0] });

    return {
        label,
        data,
        color,
        borderColor: color,
        borderWidth: 2,
        pointRadius: 0,
        showLine: true,
        fill: true,
        backgroundColor: color + "33",
    };
}
